<!-- ==========================================================================
     HERO
     ========================================================================== -->
<section class="mt-hero" aria-labelledby="mt-hero-title">
  <span class="mt-hero__wash" aria-hidden="true"></span>

  <div class="mt-hero__deco" aria-hidden="true">
    <span class="mt-orb mt-orb--pink"></span>
    <span class="mt-orb mt-orb--purple"></span>
    <span class="mt-circle mt-circle--a"></span>
    <span class="mt-circle mt-circle--b"></span>
    <span class="mt-circle mt-circle--c"></span>
    <span class="mt-dots mt-dots--tl"></span>
    <span class="mt-dots mt-dots--tr"></span>
    <span class="mt-spark" style="--sx: 7%; --sy: 46%; --sd: 0s"></span>
    <span class="mt-spark" style="--sx: 19%; --sy: 74%; --sd: 1.6s"></span>
    <span class="mt-spark" style="--sx: 90%; --sy: 62%; --sd: 2.8s"></span>
  </div>

  <div class="mt-shell mt-hero__inner">
    <span
      class="mt-eyebrow"
      data-mt-reveal="hero-eyebrow"
      [@mtReveal]="stateOf('hero-eyebrow')"
    >
      Our team
    </span>

    <h1
      class="mt-hero__title"
      id="mt-hero-title"
      data-mt-reveal="hero-title"
      [@mtReveal]="{ value: stateOf('hero-title'), params: { delay: 80 } }"
    >
      The Minds Behind Our <span class="mt-grad">Success</span>
    </h1>

    <span
      class="mt-rule"
      aria-hidden="true"
      data-mt-reveal="hero-rule"
      [@mtReveal]="{ value: stateOf('hero-rule'), params: { delay: 140 } }"
    ></span>

    <p
      class="mt-hero__lede"
      data-mt-reveal="hero-lede"
      [@mtReveal]="{ value: stateOf('hero-lede'), params: { delay: 200 } }"
    >
      Engineers, designers, testers and strategists who build and support every
      product we ship.
    </p>
  </div>
</section>

<!-- ==========================================================================
     LEADERSHIP
     ========================================================================== -->
<section class="mt-section" aria-labelledby="mt-lead-title">
  <div class="mt-shell">
    <div class="mt-panel">
      <header
        class="mt-head"
        data-mt-reveal="lead-head"
        [@mtReveal]="stateOf('lead-head')"
      >
        <span class="mt-eyebrow">Leadership</span>
        <h2 class="mt-head__title" id="mt-lead-title">
          The <em>leaders</em> behind ResilienceSoft
        </h2>
      </header>

      <ul class="mt-grid mt-grid--lead" role="list">
        <li
          class="mt-card mt-card--lead"
          *ngFor="let p of leadership; trackBy: trackPerson"
          [attr.data-accent]="p.accent"
          [attr.data-mt-reveal]="p.key"
          [@mtCard]="{ value: stateOf(p.key), params: { delay: p.delay } }"
        >
          <span class="mt-card__spot" aria-hidden="true"></span>

          <figure class="mt-card__figure">
            <div class="mt-photo mt-photo--lg">
              <span class="mt-photo__glow" aria-hidden="true"></span>
              <span class="mt-photo__ring" aria-hidden="true"></span>
              <span class="mt-photo__initials" aria-hidden="true">{{ p.initials }}</span>
              <img
                class="mt-photo__img"
                [src]="p.img"
                [alt]="p.name"
                width="260"
                height="260"
                loading="lazy"
                decoding="async"
                (error)="onPortraitError($event)"
              />
            </div>

            <figcaption class="mt-card__body">
              <h3 class="mt-card__name">{{ p.name }}</h3>
              <p class="mt-card__role">{{ p.designation }}</p>
              <p class="mt-card__blurb">{{ p.blurb }}</p>
            </figcaption>
          </figure>

          <span class="mt-card__underline" aria-hidden="true"></span>
        </li>
      </ul>
    </div>
  </div>
</section>

<!-- ==========================================================================
     THE TEAM
     ========================================================================== -->
<section class="mt-section mt-section--team" aria-labelledby="mt-team-title">
  <div class="mt-shell">
    <div class="mt-panel">
      <header
        class="mt-head"
        data-mt-reveal="team-head"
        [@mtReveal]="stateOf('team-head')"
      >
        <span class="mt-eyebrow">Our team</span>
        <h2 class="mt-head__title" id="mt-team-title">
          The people making every <em>project</em> effortless
        </h2>
      </header>

      <ul class="mt-grid mt-grid--team" role="list">
        <li
          class="mt-card mt-card--team"
          *ngFor="let p of team; trackBy: trackPerson"
          [attr.data-accent]="p.accent"
          [attr.data-mt-reveal]="p.key"
          [@mtCard]="{ value: stateOf(p.key), params: { delay: p.delay } }"
        >
          <span class="mt-card__spot" aria-hidden="true"></span>

          <div class="mt-card__top">
            <div class="mt-photo mt-photo--sm">
              <span class="mt-photo__glow" aria-hidden="true"></span>
              <span class="mt-photo__ring" aria-hidden="true"></span>
              <span class="mt-photo__initials" aria-hidden="true">{{ p.initials }}</span>
              <img
                class="mt-photo__img"
                [src]="p.img"
                [alt]="p.name"
                width="180"
                height="180"
                loading="lazy"
                decoding="async"
                (error)="onPortraitError($event)"
              />

              <span class="mt-photo__badge" [attr.title]="p.iconLabel">
                <span class="mt-sr-only">{{ p.iconLabel }}</span>

                <ng-container [ngSwitch]="p.icon">
                  <!-- leadership -->
                  <svg *ngSwitchCase="'crown'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M3 8.5l3.6 2.4L12 4l5.4 6.9L21 8.5l-1.7 9.2a1.4 1.4 0 01-1.4 1.1H6.1a1.4 1.4 0 01-1.4-1.1L3 8.5z"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <!-- management -->
                  <svg *ngSwitchCase="'brief'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect
                      x="3"
                      y="7.5"
                      width="18"
                      height="12"
                      rx="2.4"
                      stroke="currentColor"
                      stroke-width="1.7"
                    />
                    <path
                      d="M9 7.5V6a1.8 1.8 0 011.8-1.8h2.4A1.8 1.8 0 0115 6v1.5M3 12.4h18"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                  </svg>

                  <!-- design / engineering -->
                  <svg *ngSwitchCase="'code'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M8.5 8.2L4.8 12l3.7 3.8M15.5 8.2L19.2 12l-3.7 3.8M13.4 5.6l-2.8 12.8"
                      stroke="currentColor"
                      stroke-width="1.8"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <!-- PHP -->
                  <span *ngSwitchCase="'php'" class="mt-photo__badge-text">php</span>

                  <!-- Angular -->
                  <svg *ngSwitchCase="'ng'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M12 3l8.4 3 -1.3 11L12 21l-7.1-4L3.6 6 12 3z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9 15l3-7.2 3 7.2m-4.9-2.1h3.8"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <!-- React Native -->
                  <svg *ngSwitchCase="'react'" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="1.9" fill="currentColor" />
                    <ellipse cx="12" cy="12" rx="9" ry="3.6" stroke="currentColor" stroke-width="1.4" />
                    <ellipse
                      cx="12"
                      cy="12"
                      rx="9"
                      ry="3.6"
                      stroke="currentColor"
                      stroke-width="1.4"
                      transform="rotate(60 12 12)"
                    />
                    <ellipse
                      cx="12"
                      cy="12"
                      rx="9"
                      ry="3.6"
                      stroke="currentColor"
                      stroke-width="1.4"
                      transform="rotate(120 12 12)"
                    />
                  </svg>

                  <!-- operations -->
                  <svg *ngSwitchDefault viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="8.4" r="3.6" stroke="currentColor" stroke-width="1.7" />
                    <path
                      d="M4.8 20c.7-3.5 3.7-5.6 7.2-5.6s6.5 2.1 7.2 5.6"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                  </svg>
                </ng-container>
              </span>
            </div>

            <div class="mt-card__id">
              <h3 class="mt-card__name">{{ p.name }}</h3>
              <p class="mt-card__role">{{ p.designation }}</p>
            </div>
          </div>

          <p class="mt-card__blurb">{{ p.blurb }}</p>
        </li>
      </ul>
    </div>
  </div>
</section>

<!-- ==========================================================================
     CLOSING CTA
     ========================================================================== -->
<section class="mt-cta-wrap" aria-labelledby="mt-cta-title">
  <div class="mt-shell">
    <div class="mt-cta" data-mt-reveal="cta" [@mtReveal]="stateOf('cta')">
      <span class="mt-cta__flow" aria-hidden="true"></span>
      <span class="mt-cta__mesh" aria-hidden="true"></span>
      <span class="mt-cta__orb mt-cta__orb--1" aria-hidden="true"></span>
      <span class="mt-cta__orb mt-cta__orb--2" aria-hidden="true"></span>
      <span class="mt-cta__particle" style="--x: 18%; --y: 28%" aria-hidden="true"></span>
      <span class="mt-cta__particle" style="--x: 47%; --y: 78%" aria-hidden="true"></span>
      <span class="mt-cta__particle" style="--x: 66%; --y: 20%" aria-hidden="true"></span>

      <div class="mt-cta__inner">
        <span class="mt-cta__ico" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              d="M7.5 9.5a2.6 2.6 0 100-5.2 2.6 2.6 0 000 5.2zm9 0a2.6 2.6 0 100-5.2 2.6 2.6 0 000 5.2z"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linejoin="round"
            />
            <path
              d="M2.6 19.4c.5-2.9 2.5-4.6 4.9-4.6s4.4 1.7 4.9 4.6M12.6 19.4c.5-2.9 2.5-4.6 4.9-4.6s4.4 1.7 4.9 4.6"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="round"
            />
          </svg>
        </span>

        <div class="mt-cta__copy">
          <span class="mt-cta__eyebrow">Let's Work Together</span>
          <h2 class="mt-cta__title" id="mt-cta-title">
            Let's Build Something Amazing Together
          </h2>
          <p class="mt-cta__text">Have a project in mind? Let's talk and create impact.</p>
        </div>

        <a class="mt-cta__btn" routerLink="/contact-us">
          <span>Contact Us</span>
          <span class="mt-cta__btn-ico" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12h13M13 6l6 6-6 6"
                stroke="currentColor"
                stroke-width="2.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
        </a>
      </div>
    </div>
  </div>
</section>
